import { useRef, useMemo, Suspense, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const isWebGLAvailable = () => {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
  } catch (e) {
    return false;
  }
};

const Particles = ({ count, mouse }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 9;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.rotation.y = t * 0.03 + mouse.current.x * 0.15;
    ref.current.rotation.x = mouse.current.y * 0.1;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.03} color="#00F2FE" transparent opacity={0.55} sizeAttenuation depthWrite={false} />
    </points>
  );
};

const WarmParticles = ({ count, mouse }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 16;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 7;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.rotation.y = -t * 0.02 - mouse.current.x * 0.1;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.025} color="#FF5E36" transparent opacity={0.35} sizeAttenuation depthWrite={false} />
    </points>
  );
};

const Polyhedron = ({ mouse }) => {
  const group = useRef();
  const inner = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (group.current) {
      group.current.rotation.y = t * 0.18 + mouse.current.x * 0.4;
      group.current.rotation.x = Math.sin(t * 0.2) * 0.15 + mouse.current.y * 0.25;
      group.current.position.y = Math.sin(t * 0.5) * 0.15;
    }
    if (inner.current) {
      inner.current.rotation.y = -t * 0.3;
      inner.current.rotation.z = t * 0.12;
    }
  });

  return (
    <group ref={group}>
      <mesh>
        <icosahedronGeometry args={[1.7, 0]} />
        <meshPhysicalMaterial
          color="#1A202C"
          transparent
          opacity={0.25}
          roughness={0.1}
          metalness={0.4}
          transmission={0.6}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh>
        <icosahedronGeometry args={[1.72, 0]} />
        <meshBasicMaterial color="#00F2FE" wireframe transparent opacity={0.5} />
      </mesh>
      <mesh ref={inner}>
        <octahedronGeometry args={[0.75, 0]} />
        <meshBasicMaterial color="#FF5E36" wireframe transparent opacity={0.8} />
      </mesh>
    </group>
  );
};

const CameraRig = ({ mouse }) => {
  useFrame((state) => {
    state.camera.position.x += (mouse.current.x * 0.6 - state.camera.position.x) * 0.04;
    state.camera.position.y += (-mouse.current.y * 0.4 - state.camera.position.y) * 0.04;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
};

export const HeroScene = () => {
  const mouse = useRef({ x: 0, y: 0 });
  const [supported, setSupported] = useState(true);
  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const isMobile = useMemo(
    () => typeof window !== "undefined" && window.innerWidth < 768,
    []
  );

  useEffect(() => {
    setSupported(isWebGLAvailable());
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  if (!supported || reduced) {
    return (
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse at 30% 40%, rgba(0,242,254,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 60%, rgba(255,94,54,0.07) 0%, transparent 50%), #0D0D0D",
        }}
      />
    );
  }

  return (
    <div className="absolute inset-0" aria-hidden="true" data-cursor>
      <Canvas
        camera={{ position: [0, 0, 6], fov: 55 }}
        dpr={[1, 1.5]}
        gl={{ antialias: false, powerPreference: "high-performance" }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.4} />
          <pointLight position={[5, 5, 5]} intensity={1.2} color="#00F2FE" />
          <pointLight position={[-5, -3, 3]} intensity={0.8} color="#FF5E36" />
          <Particles count={isMobile ? 400 : 1400} mouse={mouse} />
          <WarmParticles count={isMobile ? 200 : 700} mouse={mouse} />
          <Polyhedron mouse={mouse} />
          <CameraRig mouse={mouse} />
        </Suspense>
      </Canvas>
    </div>
  );
};
